import { useState, useRef, KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { X, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import GiftSuggestions from "./GiftSuggestions";

export interface GiftPreferences {
  wants: string;
  has: string;
  style: string;
  registries: string[];
  priceGuidance: string;
  showInInvite: boolean;
}

interface GiftGuidanceFormProps {
  recipientName: string;
  occasion?: string;
  preferences: GiftPreferences;
  onChange: (prefs: GiftPreferences) => void;
  hideAiSuggestions?: boolean;
  /** When true, uses first-person labels and starts expanded without collapsible wrapper */
  profileMode?: boolean;
}


/** Converts between comma/newline-separated string and string[] */
function toList(str: string): string[] {
  return str
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function fromList(items: string[]): string {
  return items.join(", ");
}

/** A list input where Enter adds a new item */
function ListInput({
  items,
  onChange,
  placeholder,
  maxItems = 20,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  maxItems?: number;
}) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addItem = () => {
    const trimmed = draft.trim();
    if (!trimmed || items.length >= maxItems) return;
    onChange([...items, trimmed]);
    setDraft("");
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addItem();
    }
    if (e.key === "Backspace" && !draft && items.length > 0) {
      removeItem(items.length - 1);
    }
  };

  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="flex items-center gap-2 group"
            >
              <span className="w-1 h-1 rounded-full bg-primary/40 shrink-0" />
              <span className="text-sm font-light text-foreground flex-1">
                {item}
              </span>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="shrink-0 w-6 h-6 rounded flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-all"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (draft.trim()) addItem(); }}
        placeholder={placeholder}
        className="border-border text-sm"
      />
    </div>
  );
}

/** Label with a tooltip hint icon */
function HintLabel({ label, hint }: { label: string; hint: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1.5">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <TooltipProvider delayDuration={0}>
        <Tooltip open={open} onOpenChange={setOpen}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <HelpCircle size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs">
            {hint}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default function GiftGuidanceForm({
  recipientName,
  occasion,
  preferences,
  onChange,
  hideAiSuggestions,
  profileMode,
}: GiftGuidanceFormProps) {
  const wantsList = toList(preferences.wants);
  const hasList = toList(preferences.has);
  const styleList = toList(preferences.style);

  const update = (partial: Partial<GiftPreferences>) =>
    onChange({ ...preferences, ...partial });


  // Labels change based on mode
  const labels = profileMode
    ? {
        triggerTitle: "My gift preferences",
        triggerDesc: "Help friends pick the perfect gift for you",
        wants: "What do I want or need?",
        wantsPlaceholder: "e.g. AirPods Max, new running shoes",
        wantsHint: "Items, brands, or categories you'd love to receive",
        has: "What do I already have?",
        hasPlaceholder: "e.g. Kindle, AirPods Pro",
        hasHint: "So friends don't get you something you already own",
        style: "My style & taste",
        stylePlaceholder: "e.g. minimalist, earthy tones, tech gadgets",
        styleHint: "Colors, aesthetics, or themes you prefer",
        registries: "My registry or wishlist links",
        price: "Suggested gift budget (optional)",
        priceHint: "Give friends a sense of what's appropriate",
        showToggle: "Share these preferences when I'm tagged as a recipient",
        showToggleDesc: "Your preferences will auto-fill when someone creates a card for you",
      }
    : {
        triggerTitle: "Help your friends choose the perfect gift",
        triggerDesc: `Share what ${recipientName} loves, needs, or wants`,
        wants: `What does ${recipientName} want or need?`,
        wantsPlaceholder: "e.g. UPPAbaby Vista stroller in navy blue",
        wantsHint: "Specific items, brands, or categories",
        has: `What does ${recipientName} already have?`,
        hasPlaceholder: "e.g. crib, changing table",
        hasHint: "Avoid duplicate gifts",
        style: "Style or preferences",
        stylePlaceholder: "e.g. neutral colors, minimalist aesthetic",
        styleHint: "Colors, themes, styles they prefer",
        registries: "Registry or wishlist links",
        price: "Suggested contribution per person (optional)",
        priceHint: "Help contributors know what's appropriate",
        showToggle: "Include these preferences when inviting contributors",
        showToggleDesc: "These preferences will be shared with everyone you invite",
      };

  const formContent = (
    <div className={cn(profileMode ? "space-y-5" : "mt-4 space-y-5 animate-in slide-in-from-top-2 duration-200")}>
      {/* Wants */}
      <div className="space-y-1.5">
        <HintLabel label={labels.wants} hint={labels.wantsHint} />
        <ListInput
          items={wantsList}
          onChange={(items) => update({ wants: fromList(items) })}
          placeholder={labels.wantsPlaceholder}
        />
      </div>

      {/* Already has */}
      <div className="space-y-1.5">
        <HintLabel label={labels.has} hint={labels.hasHint} />
        <ListInput
          items={hasList}
          onChange={(items) => update({ has: fromList(items) })}
          placeholder={labels.hasPlaceholder}
        />
      </div>

      {/* Style */}
      <div className="space-y-1.5">
        <HintLabel label={labels.style} hint={labels.styleHint} />
        <ListInput
          items={styleList}
          onChange={(items) => update({ style: fromList(items) })}
          placeholder={labels.stylePlaceholder}
        />
      </div>


      {/* AI Gift Suggestions — profile mode only */}
      {profileMode && !hideAiSuggestions && (
        <div className="pt-3 border-t border-border">
          <GiftSuggestions
            recipientName={recipientName}
            occasion={occasion || "General"}
            wants={wantsList}
            has={hasList}
            style={styleList}
          />
        </div>
      )}

      {/* Toggle — profile mode only */}
      {profileMode && (
        <div className="flex items-start gap-3 pt-2 border-t border-border">
          <Checkbox
            id="showPrefs"
            checked={preferences.showInInvite}
            onCheckedChange={(checked) =>
              update({ showInInvite: checked === true })
            }
            className="mt-0.5"
          />
          <div>
            <label
              htmlFor="showPrefs"
              className="text-sm font-medium text-foreground cursor-pointer"
            >
              {labels.showToggle}
            </label>
            <p className="text-xs text-muted-foreground mt-0.5">
              {labels.showToggleDesc}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  if (profileMode) {
    return <div className="space-y-4">{formContent}</div>;
  }

  return <div className="space-y-4">{formContent}</div>;
}
